import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../lib/googleMaps';
import AdvancedMarker from './maps/AdvancedMarker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MagnifyingGlass, X as XIcon } from '@phosphor-icons/react';
import api from '../lib/api';
import { getGoogleApiFlag, trackGoogleApiUsage } from '../hooks/useGoogleApiFlags';

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

/**
 * Re-usable dialog that lets booking staff (or admin) drop a pin on Google Maps
 * and save the coordinates against a supplier.
 *
 * Permission rules (enforced server-side too):
 *   - If supplier has no lat/lng yet → anyone can set.
 *   - If already set by user X → only X or admins can change.
 *
 * Props:
 *   open: bool
 *   onClose(): fn
 *   supplier: { id, name, address?, lat?, lng?, lat_added_by_id?, lat_added_by_name? }
 *   currentUserId: string  (so we can lock UI for non-owners)
 *   isAdmin: bool
 *   onSaved(updatedSupplier): fn  (parent refresh hook)
 */
export default function SupplierLocationPicker({
  open, onClose, supplier, currentUserId, isAdmin, onSaved,
}) {
  const { isLoaded } = useGoogleMaps();
  const [mapType, setMapType] = useState('roadmap');
  const [pos, setPos] = useState({
    lat: supplier?.lat || 0,
    lng: supplier?.lng || 0,
  });
  const [address, setAddress] = useState(supplier?.address || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Re-sync when a different supplier is opened.
  React.useEffect(() => {
    setPos({ lat: supplier?.lat || 0, lng: supplier?.lng || 0 });
    setAddress(supplier?.address || '');
    setError('');
  }, [supplier?.id]);

  const hasExisting = !!(supplier?.lat && supplier?.lng);
  const ownerId = supplier?.lat_added_by_id || '';
  const isOwner = ownerId && ownerId === currentUserId;
  const canEdit = !hasExisting || isOwner || isAdmin;

  const handleClick = useCallback((e) => {
    if (!canEdit) return;
    setPos({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [canEdit]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (g) => setPos({ lat: g.coords.latitude, lng: g.coords.longitude }),
      (err) => setError('Unable to get your location: ' + err.message),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // ----- Google Places search (autocomplete) -----
  // Uses the classic AutocompleteService from the 'places' library. We call
  // getPlacePredictions on type, and PlacesService.getDetails() on select to
  // resolve lat/lng. Bias towards the current map view so Indian addresses
  // surface first.
  const mapRef = useRef(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }, [isLoaded]);

  const runSearch = (q) => {
    setSearch(q);
    if (!q.trim() || !autocompleteServiceRef.current) { setSearchResults([]); setSearchOpen(false); return; }
    // Phase 78az / 78ba — gated by `places_autocomplete_supplier_picker` flag.
    if (!getGoogleApiFlag('places_autocomplete_supplier_picker')) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    trackGoogleApiUsage('places_autocomplete_supplier_picker', 1);
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: q,
        sessionToken: sessionTokenRef.current,
        // Bias results to India. Booking is India-only at present.
        componentRestrictions: { country: 'in' },
      },
      (preds) => {
        setSearchResults(preds || []);
        setSearchOpen(true);
      },
    );
  };

  const selectPlace = (pred) => {
    setSearchOpen(false);
    setSearch(pred.description);
    if (!placesServiceRef.current && mapRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(mapRef.current);
    }
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      { placeId: pred.place_id, fields: ['geometry', 'formatted_address', 'name'], sessionToken: sessionTokenRef.current },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setPos({ lat, lng });
        if (place.formatted_address) setAddress(place.formatted_address);
        if (mapRef.current) {
          mapRef.current.panTo({ lat, lng });
          mapRef.current.setZoom(16);
        }
        // Rotate session token after a successful details call (Google billing best-practice).
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      },
    );
  };

  const handleSave = async () => {
    if (!pos.lat || !pos.lng) {
      setError('Please drop a pin on the map first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const round = (v) => Math.round(v * 1000000) / 1000000;
      const { data } = await api.put(`/booking/suppliers/${supplier.id}/location`, {
        lat: round(pos.lat),
        lng: round(pos.lng),
        address,
      });
      if (onSaved) onSaved(data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const center = (pos.lat && pos.lng) ? pos : { lat: 11.0168, lng: 76.9558 }; // Coimbatore default

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>📍 Set Location</span>
            <span className="text-sm font-normal text-[#666]">— {supplier?.name}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Permission banner */}
        {hasExisting && (
          <div
            className={`text-xs px-3 py-2 rounded ${
              canEdit ? 'bg-[#FFD700]/10 text-[#A07000] border border-[#FFD700]/40' : 'bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/30'
            }`}
            data-testid="supplier-location-permission-banner"
          >
            {isAdmin && !isOwner
              ? `🔐 Admin override — coordinates were set by ${supplier?.lat_added_by_name || 'another user'} on ${supplier?.lat_added_at?.slice(0, 10) || ''}.`
              : isOwner
              ? `✓ You set these coordinates earlier — you can edit them.`
              : `🔒 Coordinates set by ${supplier?.lat_added_by_name || 'another user'}. Only they (or admin) can edit.`}
          </div>
        )}

        {error && (
          <div className="text-xs px-3 py-2 rounded bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/30" data-testid="supplier-location-error">
            {error}
          </div>
        )}

        {/* Map type toggle */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-[#666] uppercase tracking-widest">
            {canEdit ? 'Search, tap, or use GPS to drop a pin' : 'Read-only — you cannot edit this location'}
          </p>
          <div className="flex border border-[#E5E5E5] text-[10px] uppercase tracking-widest">
            {['roadmap', 'satellite', 'hybrid'].map(t => (
              <button
                type="button"
                key={t}
                onClick={() => setMapType(t)}
                className={`px-2 py-1 ${mapType === t ? 'bg-[#002FA7] text-white' : 'bg-white text-[#666] hover:bg-[#F3F3F3]'}`}
                data-testid={`sup-loc-map-${t}`}
              >
                {t === 'roadmap' ? 'Map' : t === 'satellite' ? 'Sat' : 'Hybrid'}
              </button>
            ))}
          </div>
        </div>

        {/* Google Places search — type supplier/location/area name */}
        {canEdit && (
          <div className="relative" data-testid="sup-loc-search-wrap">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={e => runSearch(e.target.value)}
              onFocus={() => search && setSearchOpen(searchResults.length > 0)}
              placeholder="Search by supplier name, area, landmark…"
              className="w-full border border-[#E5E5E5] rounded pl-8 pr-8 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="sup-loc-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(''); setSearchResults([]); setSearchOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#FF3333] p-1"
                data-testid="sup-loc-search-clear"
                aria-label="Clear"
              >
                <XIcon size={14} weight="bold" />
              </button>
            )}
            {searchOpen && searchResults.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E5E5E5] rounded shadow-lg max-h-52 overflow-y-auto" data-testid="sup-loc-search-results">
                {searchResults.map(pred => (
                  <button
                    key={pred.place_id}
                    type="button"
                    onClick={() => selectPlace(pred)}
                    className="w-full text-left px-3 py-2 text-sm border-b border-[#F3F3F3] last:border-b-0 hover:bg-[#F9F9F9]"
                    data-testid={`sup-loc-search-opt-${pred.place_id}`}
                  >
                    <p className="font-medium truncate">{pred.structured_formatting?.main_text || pred.description}</p>
                    {pred.structured_formatting?.secondary_text && (
                      <p className="text-[10px] text-[#999] truncate">{pred.structured_formatting.secondary_text}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
            {searchOpen && searchResults.length === 0 && search.trim() && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E5E5E5] rounded shadow-lg px-3 py-3 text-xs text-[#999]">
                No matches — try another keyword
              </div>
            )}
          </div>
        )}

        {/* Map */}
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '320px' }}
            center={center}
            zoom={pos.lat ? 15 : 11}
            onClick={handleClick}
            onLoad={(m) => { mapRef.current = m; }}
            mapTypeId={mapType}
            options={{
              styles: mapType === 'roadmap' ? MAP_STYLES : undefined,
              disableDefaultUI: true, zoomControl: true, mapTypeControl: false,
              mapId: GOOGLE_MAPS_MAP_ID,
              gestureHandling: 'greedy',
            }}
          >
            {pos.lat && pos.lng && (
              <AdvancedMarker
                position={pos}
                contentHTML={`
                  <div style="transform:translate(-50%,-100%);filter:drop-shadow(0 2px 2px rgba(0,0,0,.35))">
                    <svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="#FF3333" stroke="#fff" stroke-width="2"/>
                      <circle cx="15" cy="15" r="5" fill="#fff"/>
                    </svg>
                  </div>
                `}
                title={supplier?.name}
              />
            )}
          </GoogleMap>
        ) : (
          <div className="h-[320px] bg-[#F3F3F3] flex items-center justify-center text-xs text-[#666]">Loading map…</div>
        )}

        {/* Coords + address */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Latitude</Label>
            <Input
              type="number"
              step="any"
              value={pos.lat || ''}
              disabled={!canEdit}
              onChange={e => setPos(p => ({ ...p, lat: parseFloat(e.target.value) || 0 }))}
              className="rounded text-sm"
              data-testid="sup-loc-lat"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Longitude</Label>
            <Input
              type="number"
              step="any"
              value={pos.lng || ''}
              disabled={!canEdit}
              onChange={e => setPos(p => ({ ...p, lng: parseFloat(e.target.value) || 0 }))}
              className="rounded text-sm"
              data-testid="sup-loc-lng"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Address</Label>
            <Input
              value={address}
              disabled={!canEdit}
              onChange={e => setAddress(e.target.value)}
              className="rounded text-sm"
              placeholder="Optional"
              data-testid="sup-loc-address"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-between items-center gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleUseMyLocation}
            disabled={!canEdit}
            data-testid="sup-loc-my-location"
            className="rounded-full text-xs"
          >
            📡 Use my GPS
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="sup-loc-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!canEdit || saving || !pos.lat || !pos.lng}
              className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full"
              data-testid="sup-loc-save"
            >
              {saving ? 'Saving…' : (hasExisting ? 'Update Location' : 'Set Location')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
